import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Linkedin, Github, Twitter } from 'lucide-react';
import teamMember1Img from '../assets/asem.jpg';
import teamMember2Img from '../assets/abdelrahman.jpg';
import teamMember3Img from '../assets/abdelrahman_ahmed.png';

const teamMembers = [
  {
    id: 1,
    nameEn: 'Asem Ahmed',
    nameAr: 'عاصم أحمد',
    roleEn: 'Backend Developer',
    roleAr: 'الباك إند',
    roleTr: 'Arka Uç Geliştiricisi',
    image: teamMember1Img,
    social: {
      linkedin: 'https://www.linkedin.com/in/asemabdallah/',
      github: '#',
      twitter: '#'
    }
  },
  {
    id: 2,
    nameEn: 'Abdelrahman Elsayed',
    nameAr: 'عبدالرحمن الحصين',
    roleEn: 'Frontend Developer',
    roleAr: 'الفرونت إند',
    roleTr: 'Ön Uç Geliştiricisi',
    image: teamMember2Img,
    social: {
      linkedin: 'https://www.linkedin.com/in/abdelrahman-elsayed-83a359205/',
      github: '#',
    }
  },
  {
    id: 3,
    nameEn: 'Abdelrahman Ahmed',
    nameAr: 'عبدالرحمن أحمد',
    roleEn: 'Research & DevOps',
    roleAr: 'ريسيرش دوبس',
    roleTr: 'Araştırma ve DevOps',
    image: teamMember3Img,
    social: {
      linkedin: 'https://www.linkedin.com/in/abdelrhman-ahmed01/',
      github: '#',
    }
  }
];

export default function TeamSection() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <section id="team" className="relative py-24 bg-white overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background Orbs */}
      <div className="absolute top-0 -left-40 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block py-1.5 px-4 rounded-full bg-blue-50 border border-blue-100 text-blue-600 font-semibold text-sm mb-4"
          >
            {i18n.language === 'tr' ? 'Ekibimiz' : isRTL ? 'فريق العمل' : 'Our Team'}
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6"
          >
            {i18n.language === 'tr' ? 'Arkasındaki zihinler ' : isRTL ? 'العقول المدبرة خلف ' : 'The minds behind '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              CVLogs
            </span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 leading-relaxed"
          >
            {i18n.language === 'tr' 
              ? 'Kariyerinizi geliştiren akıllı araçlar oluşturmak için yenilik ve teknolojiyi birleştiren, geliştiriciler ve yazılım mühendislerinden oluşan tutkulu bir ekip.'
              : isRTL 
              ? 'فريق شغوف من المطورين ومهندسي البرمجيات، يجمع بين الابتكار والتقنية لبناء أدوات ذكية ترتقي بمسارك المهني إلى آفاق جديدة.' 
              : 'A passionate team of developers and software engineers, combining innovation and technology to build smart tools that elevate your career.'}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-3xl p-6 shadow-lg hover:shadow-2xl hover:border-blue-300/50 transition-all duration-300"
            >
              <div className="relative mb-6 w-32 h-32 mx-auto">
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300" />
                <img 
                  src={member.image} 
                  alt={isRTL ? member.nameAr : member.nameEn}
                  className="w-full h-full object-cover rounded-full border-4 border-white shadow-md relative z-10 group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              
              <div className="text-center">
                <h3 className="text-xl font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                  {isRTL ? member.nameAr : member.nameEn}
                </h3>
                <p className="text-sm font-semibold text-indigo-500 mb-4">
                  {i18n.language === 'tr' ? member.roleTr : isRTL ? member.roleAr : member.roleEn}
                </p>
                
                <div className="flex items-center justify-center gap-3">
                  {member.social.linkedin && (
                    <a href={member.social.linkedin} className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                      <Linkedin size={16} />
                    </a>
                  )}
                  {member.social.github && (
                    <a href={member.social.github} className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-200 transition-colors">
                      <Github size={16} />
                    </a>
                  )}
                  {member.social.twitter && (
                    <a href={member.social.twitter} className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:bg-blue-50 transition-colors">
                      <Twitter size={16} />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
